import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Send, Save, Eye, CheckCircle, XCircle,
  Loader2, RefreshCw, Mail, Clock, Image, Tag, ImagePlus,
} from 'lucide-react';
import api from '@/lib/api';
import AiForgeButton from '@/components/admin/AiForgeButton';
import { SITE_NAME } from '@/config/site';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import TipTapEditor from '@/components/admin/TipTapEditor';

interface Campaign {
  id: string;
  subject: string;
  preview_text: string | null;
  html_content: string;
  intro_html: string | null;
  layout_template: string | null;
  hero_image_url: string | null;
  target_tags: string[] | null;
  status: string;
  is_ai_generated: boolean;
  ai_provider: string | null;
  product_ids: string[] | null;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  scheduled_send_at: string | null;
  sent_at: string | null;
  created_at: string;
  schedule_id: string | null;
}

interface AiSettings {
  has_google_key: boolean;
  has_hf_token: boolean;
  has_pollinations_key: boolean;
  default_text_provider: string;
  default_image_provider: string;
}

interface TrackingStats {
  opens: number;
  unique_opens: number;
  open_rate: number;
  clicks: number;
  unique_clicks: number;
  click_rate: number;
}

const CAMPAIGN_TYPES = [
  { label: 'New Arrivals', value: 'new_arrivals' },
  { label: 'Seasonal Guide', value: 'seasonal_planting' },
  { label: 'Featured Products', value: 'featured_products' },
  { label: 'Category Spotlight', value: 'category_spotlight' },
  { label: 'Tips & Guides', value: 'growing_tips' },
  { label: 'Promotions & Sales', value: 'promotions' },
  { label: 'Custom', value: 'custom' },
];

const LAYOUT_TEMPLATES = [
  { label: 'Classic', value: 'classic', description: 'Hero image + editorial flow' },
  { label: 'Product Grid', value: 'product_grid', description: '2-column product cards' },
  { label: 'Single Spotlight', value: 'single_spotlight', description: 'One featured product' },
  { label: 'Seasonal Guide', value: 'seasonal_guide', description: 'Seasonal picks and recommendations' },
  { label: 'Minimal', value: 'minimal', description: 'Text-only, no images' },
];

const TEXT_PROVIDERS = [
  { label: 'Google (Gemini)', value: 'google' },
  { label: 'HuggingFace', value: 'huggingface' },
  { label: 'Pollinations', value: 'pollinations' },
];

const IMAGE_PROVIDERS = [
  { label: 'Google', value: 'google' },
  { label: 'HuggingFace', value: 'huggingface' },
  { label: 'Pollinations', value: 'pollinations' },
];

const SUGGESTED_TAGS = [
  'vegetables', 'flowers', 'herbs', 'organic', 'indoor',
  'perennials', 'fruit', 'seeds',
];

type GenerateStep = 'idle' | 'generating_text' | 'generating_image' | 'done' | 'error';

export default function NewsletterCampaignEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [scheduledSendAt, setScheduledSendAt] = useState('');
  const [layoutTemplate, setLayoutTemplate] = useState('classic');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [generateImageChecked, setGenerateImageChecked] = useState(true);
  const [targetTags, setTargetTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // AI generation state
  const [showAiPanel, setShowAiPanel] = useState(isNew);
  const [campaignType, setCampaignType] = useState('seasonal_planting');
  const [provider, setProvider] = useState('google');
  const [imageProvider, setImageProvider] = useState('pollinations');
  const [imageModel, setImageModel] = useState('');
  const [imageModels, setImageModels] = useState<{ label: string; value: string }[]>([]);
  const [promptContext, setPromptContext] = useState('');
  const [generateStep, setGenerateStep] = useState<GenerateStep>('idle');
  const [generateError, setGenerateError] = useState('');

  // Image studio state
  const [showImageStudio, setShowImageStudio] = useState(false);

  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  // Tracking stats
  const [trackingStats, setTrackingStats] = useState<TrackingStats | null>(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);

  const isMinimalLayout = layoutTemplate === 'minimal';

  // Load AI settings
  useEffect(() => {
    api.get<AiSettings>('/admin/ai/settings').then((res) => {
      setProvider(res.default_text_provider || 'google');
      setImageProvider(res.default_image_provider || 'pollinations');
    }).catch(() => {});
  }, []);

  // Load image models when provider is pollinations
  useEffect(() => {
    if (imageProvider !== 'pollinations') {
      setImageModels([]);
      setImageModel('');
      return;
    }
    api.get<{ models: { id: string; name: string }[] }>('/admin/ai/models/image')
      .then((res) => {
        const opts = (res.models || []).map((m) => ({ label: m.name, value: m.id }));
        setImageModels(opts);
        if (opts.length > 0 && !imageModel) setImageModel(opts[0].value);
      })
      .catch(() => {});
  }, [imageProvider]);

  // Load existing campaign
  useEffect(() => {
    if (isNew) return;
    setIsLoading(true);
    api.get<Campaign>(`/admin/newsletter/campaigns/${id}`)
      .then((data) => {
        setCampaign(data);
        setSubject(data.subject);
        setPreviewText(data.preview_text || '');
        setHtmlContent(data.html_content);
        setLayoutTemplate(data.layout_template || 'classic');
        setHeroImageUrl(data.hero_image_url || '');
        setTargetTags(data.target_tags || []);
        setScheduledSendAt(data.scheduled_send_at ? data.scheduled_send_at.slice(0, 16) : '');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load campaign'))
      .finally(() => setIsLoading(false));
  }, [id, isNew]);

  // Load tracking stats when campaign is sent
  useEffect(() => {
    if (!campaign || campaign.status !== 'sent') return;
    setIsLoadingTracking(true);
    api.get<TrackingStats>(`/admin/newsletter/campaigns/${campaign.id}/tracking`)
      .then((stats) => setTrackingStats(stats))
      .catch(() => {})
      .finally(() => setIsLoadingTracking(false));
  }, [campaign?.id, campaign?.status]);

  const handleAiGenerate = async () => {
    setGenerateStep('generating_text');
    setGenerateError('');
    try {
      const shouldGenerateImage = generateImageChecked && !isMinimalLayout;

      const result = await api.post<{
        subject: string;
        preview_text: string;
        html_content?: string;
        intro_html?: string;
        product_ids: string[];
        image_prompt?: string;
        hero_image_url?: string;
        layout_template?: string;
      }>('/admin/newsletter/campaigns/generate', {
        campaign_type: campaignType,
        provider,
        prompt_context: promptContext || undefined,
        layout_template: layoutTemplate,
        generate_image: shouldGenerateImage,
        image_provider: shouldGenerateImage ? imageProvider : undefined,
        image_model: shouldGenerateImage && imageProvider === 'pollinations' && imageModel ? imageModel : undefined,
      });

      setSubject(result.subject);
      setPreviewText(result.preview_text);
      if (result.html_content) setHtmlContent(result.html_content);
      if (result.image_prompt) setImagePrompt(result.image_prompt);

      if (shouldGenerateImage && result.hero_image_url) {
        setGenerateStep('generating_image');
        setHeroImageUrl(result.hero_image_url);
      }

      if (result.layout_template) setLayoutTemplate(result.layout_template);

      setGenerateStep('done');
      setTimeout(() => setShowAiPanel(false), 1500);
    } catch (err: any) {
      setGenerateError(err.message || 'Generation failed');
      setGenerateStep('error');
    }
  };

  const handleRegenerateImage = async () => {
    if (!imagePrompt) return;
    setGenerateStep('generating_image');
    setGenerateError('');
    try {
      const result = await api.post<{ hero_image_url: string }>('/admin/newsletter/campaigns/generate-image', {
        image_prompt: imagePrompt,
        image_provider: imageProvider,
        image_model: imageProvider === 'pollinations' && imageModel ? imageModel : undefined,
      });
      setHeroImageUrl(result.hero_image_url);
      setGenerateStep('done');
    } catch (err: any) {
      setGenerateError(err.message || 'Image generation failed');
      setGenerateStep('error');
    }
  };

  const handleSave = async () => {
    if (!subject.trim() || !htmlContent.trim()) {
      setError('Subject and content are required');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      if (isNew || !campaign) {
        const created = await api.post<Campaign>('/admin/newsletter/campaigns', {
          subject,
          preview_text: previewText || undefined,
          html_content: htmlContent,
          layout_template: layoutTemplate,
          hero_image_url: heroImageUrl || undefined,
          target_tags: targetTags.length > 0 ? targetTags : undefined,
          is_ai_generated: generateStep === 'done',
          ai_provider: generateStep === 'done' ? provider : undefined,
          scheduled_send_at: scheduledSendAt || undefined,
        });
        setSuccessMessage('Campaign saved');
        navigate(`/admin/newsletter/campaigns/${created.id}/edit`, { replace: true });
      } else {
        const updated = await api.put<Campaign>(`/admin/newsletter/campaigns/${campaign.id}`, {
          subject,
          preview_text: previewText || undefined,
          html_content: htmlContent,
          layout_template: layoutTemplate,
          hero_image_url: heroImageUrl || undefined,
          target_tags: targetTags.length > 0 ? targetTags : undefined,
          scheduled_send_at: scheduledSendAt || undefined,
        });
        setCampaign(updated);
        setSuccessMessage('Campaign updated');
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !campaign) return;
    setIsSendingTest(true);
    try {
      await api.post(`/admin/newsletter/campaigns/${campaign.id}/send-test`, { email: testEmail });
      setSuccessMessage(`Test email sent to ${testEmail}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to send test');
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleApprove = async () => {
    if (!campaign) return;
    try {
      const updated = await api.post<Campaign>(`/admin/newsletter/campaigns/${campaign.id}/approve`);
      setCampaign(updated);
      setSuccessMessage('Campaign approved');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to approve');
    }
  };

  const handleSend = async () => {
    if (!campaign) return;
    if (!confirm('Send this campaign to all active subscribers? This cannot be undone.')) return;
    try {
      const result = await api.post<{ message: string; total_recipients: number }>(
        `/admin/newsletter/campaigns/${campaign.id}/send`
      );
      setSuccessMessage(result.message);
      setCampaign({ ...campaign, status: 'sending', total_recipients: result.total_recipients });
    } catch (err: any) {
      setError(err.message || 'Failed to send');
    }
  };

  const handleRegenerate = async () => {
    if (!campaign) return;
    setGenerateStep('generating_text');
    setGenerateError('');
    try {
      const shouldGenerateImage = generateImageChecked && !isMinimalLayout;
      const updated = await api.post<Campaign>(
        `/admin/newsletter/campaigns/${campaign.id}/regenerate`,
        {
          provider,
          layout_template: layoutTemplate,
          generate_image: shouldGenerateImage,
          image_provider: shouldGenerateImage ? imageProvider : undefined,
          image_model: shouldGenerateImage && imageProvider === 'pollinations' && imageModel ? imageModel : undefined,
        }
      );
      setCampaign(updated);
      setSubject(updated.subject);
      setPreviewText(updated.preview_text || '');
      setHtmlContent(updated.html_content);
      if (updated.hero_image_url) setHeroImageUrl(updated.hero_image_url);
      if (updated.layout_template) setLayoutTemplate(updated.layout_template);
      setGenerateStep('done');
      setSuccessMessage('Content regenerated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setGenerateError(err.message || 'Regeneration failed');
      setGenerateStep('error');
    }
  };

  const toggleTargetTag = (tag: string) => {
    setTargetTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const isGenerating = generateStep === 'generating_text' || generateStep === 'generating_image';

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  const isEditable = !campaign || ['draft', 'pending_approval'].includes(campaign.status);
  const canSend = campaign && ['approved', 'draft'].includes(campaign.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/newsletter/campaigns')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew ? 'New Campaign' : 'Edit Campaign'}
          </h1>
          {campaign && (
            <Badge variant={
              campaign.status === 'sent' ? 'green' :
              campaign.status === 'sending' ? 'blue' :
              campaign.status === 'approved' ? 'blue' :
              campaign.status === 'pending_approval' ? 'yellow' :
              campaign.status === 'failed' ? 'red' : 'gray'
            }>
              {campaign.status.replace('_', ' ')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditable && (
            <Button variant="secondary" onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Draft'}
            </Button>
          )}
          {campaign && isEditable && (
            <Button variant="secondary" onClick={handleApprove}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve
            </Button>
          )}
          {canSend && (
            <Button onClick={handleSend}>
              <Send className="h-4 w-4 mr-2" />
              Send Now
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg" role="alert">
          {error}
          <button onClick={() => setError(null)} className="float-right text-red-600 hover:text-red-800">&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* AI Generation Panel */}
      {isEditable && (
        <Card padding="lg">
          <button
            onClick={() => setShowAiPanel(!showAiPanel)}
            className="flex items-center gap-2 w-full text-left"
          >
            <Sparkles className="h-5 w-5 text-purple-600" />
            <span className="font-semibold text-gray-900">Generate with AI</span>
            <span className="text-sm text-gray-500 ml-2">
              {showAiPanel ? '(click to collapse)' : '(click to expand)'}
            </span>
          </button>

          {showAiPanel && (
            <div className="mt-4 space-y-5">
              {/* Layout Template Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Layout Template</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {LAYOUT_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.value}
                      type="button"
                      onClick={() => {
                        setLayoutTemplate(tpl.value);
                        if (tpl.value === 'minimal') setGenerateImageChecked(false);
                        else setGenerateImageChecked(true);
                      }}
                      className={[
                        'rounded-lg border-2 p-3 text-left transition-all',
                        layoutTemplate === tpl.value
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-white hover:border-gray-300',
                      ].join(' ')}
                    >
                      <p className={[
                        'text-sm font-medium',
                        layoutTemplate === tpl.value ? 'text-green-800' : 'text-gray-800',
                      ].join(' ')}>
                        {tpl.label}
                      </p>
                      <p className={[
                        'text-xs mt-0.5',
                        layoutTemplate === tpl.value ? 'text-green-600' : 'text-gray-500',
                      ].join(' ')}>
                        {tpl.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Type</label>
                  <Select
                    value={campaignType}
                    onChange={(e) => setCampaignType(e.target.value)}
                    options={CAMPAIGN_TYPES}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text AI Provider</label>
                  <Select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    options={TEXT_PROVIDERS}
                  />
                </div>
              </div>

              {/* Image generation options */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Image className="h-4 w-4 text-gray-500" />
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={generateImageChecked}
                      disabled={isMinimalLayout}
                      onChange={(e) => setGenerateImageChecked(e.target.checked)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className={[
                      'text-sm font-medium',
                      isMinimalLayout ? 'text-gray-400' : 'text-gray-700',
                    ].join(' ')}>
                      Generate hero image
                    </span>
                    {isMinimalLayout && (
                      <span className="text-xs text-gray-400">(disabled for minimal layout)</span>
                    )}
                  </label>
                </div>

                {generateImageChecked && !isMinimalLayout && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Image Provider</label>
                      <Select
                        value={imageProvider}
                        onChange={(e) => setImageProvider(e.target.value)}
                        options={IMAGE_PROVIDERS}
                      />
                    </div>
                    {imageProvider === 'pollinations' && imageModels.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
                        <Select
                          value={imageModel}
                          onChange={(e) => setImageModel(e.target.value)}
                          options={imageModels}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Context (optional)
                </label>
                <textarea
                  value={promptContext}
                  onChange={(e) => setPromptContext(e.target.value)}
                  placeholder="e.g., Focus on upcoming promotions, seasonal highlights, new arrivals..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-3">
                {campaign ? (
                  <Button
                    onClick={handleRegenerate}
                    disabled={isGenerating}
                    variant="secondary"
                  >
                    {generateStep === 'generating_text' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating content...</>
                    ) : generateStep === 'generating_image' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating hero image...</>
                    ) : (
                      <><RefreshCw className="h-4 w-4 mr-2" />Regenerate</>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleAiGenerate}
                    disabled={isGenerating}
                  >
                    {generateStep === 'generating_text' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating content...</>
                    ) : generateStep === 'generating_image' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating hero image...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" />Generate Email</>
                    )}
                  </Button>
                )}
                {generateStep === 'done' && (
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" /> Generated successfully
                  </span>
                )}
                {generateStep === 'error' && (
                  <span className="flex items-center gap-1 text-sm text-red-600">
                    <XCircle className="h-4 w-4" /> {generateError}
                  </span>
                )}
              </div>

              {/* Hero image preview after generation */}
              {heroImageUrl && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                      <Image className="h-3.5 w-3.5" />
                      Hero Image
                    </span>
                    <div className="flex items-center gap-3">
                      {imagePrompt && (
                        <button
                          type="button"
                          onClick={handleRegenerateImage}
                          disabled={isGenerating}
                          className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 disabled:opacity-50"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Regenerate
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowImageStudio(true)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                      >
                        <ImagePlus className="h-3 w-3" />
                        Open Studio
                      </button>
                    </div>
                  </div>
                  <img
                    src={heroImageUrl}
                    alt="Hero image preview"
                    className="w-full max-h-64 object-cover"
                  />
                  <div className="px-3 py-2 bg-gray-50">
                    <p className="text-xs text-gray-500 truncate">{heroImageUrl}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Subject & Preview Text */}
      <Card padding="lg">
        <div className="space-y-4">
          {/* Layout template indicator for saved campaigns */}
          {!isNew && campaign?.layout_template && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Layout:</span>
              <span className="text-xs bg-green-100 text-green-800 rounded px-2 py-0.5 font-medium">
                {LAYOUT_TEMPLATES.find((t) => t.value === campaign.layout_template)?.label ?? campaign.layout_template}
              </span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Your email subject line..."
              disabled={!isEditable}
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{subject.length}/200 characters</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preview Text</label>
            <Input
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="Text shown in email inbox preview..."
              disabled={!isEditable}
              maxLength={200}
            />
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <p className="text-xs font-medium text-gray-500 mb-1">Personalization Tags</p>
            <div className="flex flex-wrap gap-2">
              {['{{first_name}}', '{{last_name}}', '{{email}}'].map((tag) => (
                <code
                  key={tag}
                  className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 text-purple-700 cursor-default select-all"
                >
                  {tag}
                </code>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">Use these in subject or content. Replaced with subscriber data at send time.</p>
          </div>
          {isEditable && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="h-4 w-4 inline mr-1" />
                Schedule Send (optional)
              </label>
              <input
                type="datetime-local"
                value={scheduledSendAt}
                onChange={(e) => setScheduledSendAt(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Target Subscriber Tags */}
      <Card padding="lg">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="h-4 w-4 text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900">Target Subscriber Tags</h2>
          <span className="text-xs text-gray-400">(optional - leave blank to send to all)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_TAGS.map((tag) => {
            const selected = targetTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                disabled={!isEditable}
                onClick={() => toggleTargetTag(tag)}
                className={[
                  'rounded-full px-3 py-1 text-sm font-medium border transition-all',
                  selected
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:text-green-700',
                  !isEditable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                ].join(' ')}
              >
                {tag}
              </button>
            );
          })}
        </div>
        {targetTags.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            Sending to subscribers tagged: {targetTags.join(', ')}
          </p>
        )}
      </Card>

      {/* Hero Image (manual entry for non-AI-generated) */}
      {isEditable && (
        <Card padding="lg">
          <div className="flex items-center gap-2 mb-3">
            <Image className="h-4 w-4 text-gray-500" />
            <h2 className="text-base font-semibold text-gray-900">Hero Image</h2>
            <span className="text-xs text-gray-400">(optional)</span>
          </div>
          <div className="space-y-3">
            <Input
              value={heroImageUrl}
              onChange={(e) => setHeroImageUrl(e.target.value)}
              placeholder="https://..."
              disabled={!isEditable}
            />
            {heroImageUrl && (
              <img
                src={heroImageUrl}
                alt="Hero preview"
                className="rounded-lg max-h-48 object-cover border border-gray-200"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
          </div>
        </Card>
      )}

      {/* Email Content Editor */}
      <Card padding="lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Email Content</h2>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Eye className="h-4 w-4" />
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>

        {showPreview ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-green-800 text-white text-center py-4 font-bold text-lg">
              {SITE_NAME}
            </div>
            {heroImageUrl && (
              <img src={heroImageUrl} alt="Hero" className="w-full max-h-72 object-cover" />
            )}
            <div
              className="p-6 prose prose-stone max-w-none"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
            <div className="border-t border-gray-200 p-4 text-center text-xs text-gray-500">
              <p>{SITE_NAME}</p>
              <p>&nbsp;</p>
              <a href="#" className="text-gray-400 underline">Unsubscribe from newsletter</a>
            </div>
          </div>
        ) : (
          <TipTapEditor
            content={htmlContent}
            onChange={setHtmlContent}
            placeholder="Write your email content here..."
            editable={isEditable}
          />
        )}
      </Card>

      {/* Send Test Email */}
      {campaign && isEditable && (
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Send Test Email</h2>
          <div className="flex items-center gap-3">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address..."
              className="flex-1"
            />
            <Button
              variant="secondary"
              onClick={handleSendTest}
              disabled={isSendingTest || !testEmail}
            >
              <Mail className="h-4 w-4 mr-2" />
              {isSendingTest ? 'Sending...' : 'Send Test'}
            </Button>
          </div>
        </Card>
      )}

      {/* Campaign Info (for existing campaigns) */}
      {campaign && campaign.status === 'sent' && (
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Send Results</h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-gray-600">Total Recipients</p>
              <p className="text-2xl font-bold">{campaign.total_recipients}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Sent</p>
              <p className="text-2xl font-bold text-green-600">{campaign.sent_count}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{campaign.failed_count}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Tracking Stats */}
      {campaign && campaign.status === 'sent' && (
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Engagement Tracking</h2>
          {isLoadingTracking ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : trackingStats ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Opens</p>
                <p className="text-2xl font-bold text-blue-800">{trackingStats.opens}</p>
                <p className="text-xs text-blue-600 mt-0.5">{trackingStats.unique_opens} unique</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Open Rate</p>
                <p className="text-2xl font-bold text-blue-800">
                  {trackingStats.open_rate != null
                    ? `${(trackingStats.open_rate * 100).toFixed(1)}%`
                    : '—'}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Clicks</p>
                <p className="text-2xl font-bold text-green-800">{trackingStats.clicks}</p>
                <p className="text-xs text-green-600 mt-0.5">{trackingStats.unique_clicks} unique</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Click Rate</p>
                <p className="text-2xl font-bold text-green-800">
                  {trackingStats.click_rate != null
                    ? `${(trackingStats.click_rate * 100).toFixed(1)}%`
                    : '—'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No tracking data available yet.</p>
          )}
        </Card>
      )}
      <AiForgeButton
        isOpen={showImageStudio}
        onClose={() => setShowImageStudio(false)}
        context="newsletter"
        contextName={subject}
        initialPrompt={imagePrompt}
        referenceImages={heroImageUrl ? [{ url: heroImageUrl }] : undefined}
        onImageGenerated={(img) => {
          setHeroImageUrl(img.url);
          setShowImageStudio(false);
        }}
      />
    </div>
  );
}
